import { StrictMode, lazy, Suspense } from "react";
import { createRoot } from "react-dom/client";
import { createBrowserRouter, RouterProvider } from "react-router";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { AppLayout } from "@/components/layout";
import { PageSkeleton } from "@/components/cs";
import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { setupNative } from "@/lib/native";
import { HomePage } from "@/pages/home";
import "./index.css";

// Heavier pages load on demand.
const MatchPage = lazy(() => import("@/pages/match"));
const PlayerPage = lazy(() => import("@/pages/player"));
const VideosPage = lazy(() => import("@/pages/videos"));
const AdminRoutes = lazy(() => import("@/pages/admin"));
const PrivacyPage = lazy(() => import("@/pages/legal").then((m) => ({ default: m.PrivacyPage })));
const TermsPage = lazy(() => import("@/pages/legal").then((m) => ({ default: m.TermsPage })));
const ConnectPage = lazy(() => import("@/pages/connect"));

const page = (el: React.ReactNode) => <Suspense fallback={<PageSkeleton />}>{el}</Suspense>;

const router = createBrowserRouter([
  {
    element: <AppLayout />,
    children: [
      { index: true, element: <HomePage /> },
      { path: "match/:id", element: page(<MatchPage />) },
      { path: "player/:steamId", element: page(<PlayerPage />) },
      { path: "videos", element: page(<VideosPage />) },
      { path: "connect", element: page(<ConnectPage />) },
      { path: "admin/*", element: page(<AdminRoutes />) },
      { path: "privacy", element: page(<PrivacyPage />) },
      { path: "terms", element: page(<TermsPage />) },
      { path: "*", element: <div className="py-20 text-center text-muted-foreground">Страница не найдена</div> },
    ],
  },
]);

const queryClient = new QueryClient({
  defaultOptions: { queries: { retry: (n, err) => n < 2 && !(err as { status?: number }).status, refetchOnWindowFocus: false } },
});

setupNative(); // no-op in a browser
createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider delayDuration={200}>
        <RouterProvider router={router} />
        <Toaster position="top-center" />
      </TooltipProvider>
    </QueryClientProvider>
  </StrictMode>,
);
